import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import { useAuth } from '../services/firebase/auth.js';
import { NotificationBus } from '../services/notification-bus.js';
import { buildSentryRequestContext } from '../utils/auth-helpers.js';
import '../components/icons/info-icon.js';
import '../components/icons/success-icon.js';
import '../components/icons/warning-icon.js';

class SignInPage extends LitElement {
    static properties = {
        email: { type: String },
        loading: { type: Boolean },
        linkSent: { type: Boolean },
        completingMagicLink: { type: Boolean, state: true },
        magicLinkError: { type: Boolean, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.email = '';
        this.loading = false;
        this.linkSent = false;
        this.completingMagicLink = false;
        this.magicLinkError = false;
    }

    connectedCallback() {
        super.connectedCallback();
        const context = window.sessionStorage.getItem('magicLinkFailed');
        if (context) {
            window.sessionStorage.removeItem('magicLinkFailed');
            const { isEmailSignInLink } = useAuth();
            if (isEmailSignInLink(window.location.href)) {
                this.completingMagicLink = true;
            }
        }
    }

    handleEmailInput(e) {
        this.email = e.target.value;
    }

    validateEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    async handleSubmit(e) {
        e.preventDefault();

        // Reset state
        this.linkSent = false;

        // Validate email
        const trimmedEmail = this.email.trim();
        if (!trimmedEmail) {
            NotificationBus.warning('Please enter your email address', 'sign-in-page');
            return;
        }

        if (!this.validateEmail(trimmedEmail)) {
            NotificationBus.warning('Please enter a valid email address', 'sign-in-page');
            return;
        }

        // Set loading state
        this.loading = true;

        // Get redirect URL (preserve original destination if exists)
        const redirect = window.sessionStorage.getItem('authRedirect') || '/';
        const fullRedirectUrl = `${window.location.origin}${window.location.pathname}#${redirect}`;

        try {
            const { requestEmailLink } = useAuth();

            // Request magic link
            await requestEmailLink(trimmedEmail, fullRedirectUrl);

            // Show success message
            this.linkSent = true;
            this.loading = false;
        } catch (error) {
            console.error('Error requesting magic link:', error);

            Sentry.captureException(
                error,
                buildSentryRequestContext({
                    error,
                    email: trimmedEmail,
                    redirectUrl: fullRedirectUrl,
                    feature: 'magic-link-request',
                })
            );

            // Show persistent error notification that user must acknowledge
            NotificationBus.persistent(
                'There was a problem requesting your login link. Please try again later.',
                'error',
                'sign-in-page'
            );

            this.loading = false;
        }
    }

    async handleMagicLinkComplete(e) {
        e.preventDefault();
        const trimmedEmail = this.email.trim();

        if (!trimmedEmail || !this.validateEmail(trimmedEmail)) {
            NotificationBus.warning('Please enter a valid email address', 'sign-in-page');
            return;
        }

        this.loading = true;

        try {
            const { signInWithEmail } = useAuth();

            // Store email for future magic links from this browser
            window.localStorage.setItem('emailForSignIn', trimmedEmail);

            await signInWithEmail(trimmedEmail);

            // Redirect to intended destination, dropping any leftover magic-link
            // search params (apiKey, oobCode, mode, continueUrl, lang) that
            // survived the no-email completion path.
            const redirect = window.sessionStorage.getItem('authRedirect') || '/';
            window.sessionStorage.removeItem('authRedirect');
            window.history.replaceState(
                null,
                '',
                `${window.location.pathname}#${redirect}`
            );
            window.location.hash = redirect;
        } catch (error) {
            this.loading = false;
            this.completingMagicLink = false;
            this.magicLinkError = true;

            Sentry.captureException(
                error,
                buildSentryRequestContext({
                    error,
                    email: trimmedEmail,
                    redirectUrl: window.location.href,
                    feature: 'magic-link-completion',
                })
            );

            // Clean URL params since the link is no longer valid
            window.history.replaceState(null, '', `${window.location.pathname}#/sign-in`);
        }
    }

    _renderMagicLinkCompletion() {
        return html`
            <div class="alert alert-row alert-info mb-4">
                <info-icon class="shrink-0"></info-icon>
                <span class="text-sm text-left">
                    You opened your sign-in link in a different browser or device. Enter your email
                    address below to continue.
                </span>
            </div>

            <form @submit="${this.handleMagicLinkComplete}" class="space-y-4">
                <div class="form-control">
                    <label for="email-input" class="label">
                        <span class="label-text">Email Address</span>
                    </label>
                    <input
                        id="email-input"
                        type="email"
                        .value="${this.email}"
                        @input="${this.handleEmailInput}"
                        placeholder="your.email@example.com"
                        class="input input-bordered w-full"
                        ?disabled="${this.loading}"
                        required
                    />
                </div>

                <button type="submit" class="btn btn-primary w-full" ?disabled="${this.loading}">
                    ${this.loading
                        ? html`<span class="loading loading-spinner loading-sm"></span> Signing
                              in...`
                        : 'Complete Sign In'}
                </button>
            </form>

            <p class="text-sm text-center text-gray-500 mt-4">
                Wrong email?
                <button
                    @click="${() => {
                        this.completingMagicLink = false;
                    }}"
                    class="text-peppermint hover:underline"
                >
                    Request a new sign-in link instead
                </button>
            </p>
        `;
    }

    _renderNormalSignIn() {
        return html`
            <p class="text-center text-gray-600 mb-8">Enter your email to receive a sign-in link</p>

            ${this.linkSent
                ? html`
                      <div class="alert alert-row alert-success mb-4" role="alert">
                          <success-icon class="shrink-0"></success-icon>
                          <div class="text-left">
                              <h3 class="font-bold">Check your email!</h3>
                              <div class="text-sm">
                                  We've sent a sign-in link to
                                  <strong>${this.email}</strong>. Click the link in the email to
                                  sign in.
                              </div>
                          </div>
                      </div>

                      <p class="text-sm text-center text-gray-500 mt-4">
                          Didn't receive the email? Check your spam folder or
                          <button
                              @click="${() => {
                                  this.linkSent = false;
                              }}"
                              class="text-peppermint hover:underline"
                          >
                              try again
                          </button>
                      </p>
                  `
                : html`
                      <form @submit="${this.handleSubmit}" class="space-y-4">
                          <div class="form-control">
                              <label for="email-input" class="label">
                                  <span class="label-text">Email Address</span>
                              </label>
                              <input
                                  id="email-input"
                                  type="email"
                                  .value="${this.email}"
                                  @input="${this.handleEmailInput}"
                                  placeholder="your.email@example.com"
                                  class="input input-bordered w-full"
                                  ?disabled="${this.loading}"
                                  required
                              />
                          </div>

                          <button
                              type="submit"
                              class="btn btn-primary w-full"
                              ?disabled="${this.loading}"
                          >
                              ${this.loading
                                  ? html`<span class="loading loading-spinner loading-sm"></span>
                                        Sending...`
                                  : 'Send Magic Link'}
                          </button>
                      </form>

                      <div class="mt-6 text-center text-sm text-gray-500">
                          <p>
                              You'll receive an email with a secure link to sign in. The link will
                              expire in 1 hour.
                          </p>
                      </div>
                  `}
        `;
    }

    render() {
        return html`
            <div class="flex flex-col items-center justify-center min-h-[60vh] px-4">
                <div class="w-full max-w-md">
                    <h1 class="text-3xl font-bold mb-2 text-center">
                        ${this.completingMagicLink ? 'Complete Sign In' : 'Sign In'}
                    </h1>

                    ${this.magicLinkError
                        ? html`
                              <div class="alert alert-row alert-warning mb-4">
                                  <warning-icon class="shrink-0"></warning-icon>
                                  <span class="text-sm text-left">
                                      Your sign-in link has expired or is invalid. Please request a
                                      new one below.
                                  </span>
                              </div>
                          `
                        : ''}
                    ${this.completingMagicLink
                        ? this._renderMagicLinkCompletion()
                        : this._renderNormalSignIn()}
                </div>
            </div>
        `;
    }
}

customElements.define('sign-in-page', SignInPage);
